import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";

// Public Q&A hub — lists every published answer. Designed to be a Google
// landing page for the /ask URL itself ("opencsv answers" / "csv help" etc.)
// and an easy navigation for users who want to browse all answers.

export default function AskHub() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/ask")
            .then(({ data }) => setItems(data.questions || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = q ? items.filter((i) => (i.title + " " + (i.summary || "")).toLowerCase().includes(q.toLowerCase())) : items;

    return (
        <>
            <Helmet>
                <title>OpenCSV Answers — fixes for CSV import problems</title>
                <meta name="description" content="Plain-English answers to common CSV problems: Excel showing strange characters, Asana refusing imports, Google Sheets parsing dates weirdly. All fixable in seconds with OpenCSV." />
                <link rel="canonical" href="https://opencsv.io/ask" />
            </Helmet>
            <article className="px-6 sm:px-12 pt-12 pb-24 max-w-5xl mx-auto">
                <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">/ ask</div>
                <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-2">CSV problems, answered.</h1>
                <p className="text-base text-ink-soft mt-4 max-w-2xl">Every common reason your CSV won't import, won't open, or won't behave — with a one-click fix using OpenCSV.</p>

                <div className="mt-8 max-w-xl">
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                        <input
                            type="search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search questions…"
                            className="w-full border border-ink/20 bg-white pl-10 pr-3 py-2.5 text-sm"
                            data-testid="ask-search"
                        />
                    </div>
                </div>

                <div className="mt-10 grid grid-cols-1 gap-3">
                    {loading ? (
                        <div className="text-sm text-ink-muted">Loading…</div>
                    ) : filtered.length === 0 ? (
                        <div className="text-sm text-ink-muted">{items.length === 0 ? "Answers coming soon. Bookmark this page." : "No matches."}</div>
                    ) : (
                        filtered.map((it) => (
                            <Link key={it.slug} to={`/ask/${it.slug}`} className="border border-ink/15 bg-white p-4 hover:border-cobalt transition-colors" data-testid={`ask-item-${it.slug}`}>
                                <div className="font-display font-bold tracking-tight text-lg leading-tight">{it.title}</div>
                                {it.summary && <p className="text-sm text-ink-soft mt-1.5 line-clamp-2">{it.summary}</p>}
                            </Link>
                        ))
                    )}
                </div>

                <div className="mt-16 border-t border-ink/10 pt-10">
                    <h2 className="font-display font-black tracking-tighter text-2xl">Stop fighting your CSVs.</h2>
                    <p className="text-sm text-ink-soft mt-2 max-w-xl">OpenCSV opens, fixes, and exports your file in the right format for whatever tool you're importing into. Free to view, $9.99/mo Pro to edit & export — 14-day free trial.</p>
                    <Link to="/app" className="btn-brutal mt-4 inline-flex items-center bg-cobalt text-white px-5 py-2.5 ui-heading text-sm font-semibold border border-cobalt" data-testid="ask-hub-cta">Open the editor →</Link>
                </div>
            </article>
        </>
    );
}
